import React, {useEffect, useState} from 'react';
import {Controller, useFieldArray, useForm} from "react-hook-form";
import {SortableContainer, SortableElement, SortableHandle} from "react-sortable-hoc";
import {NavLink, useNavigate} from "react-router-dom";
import {circuit} from "../../../models/circuit";
import {circuitResultType} from "../../../types/Api/CircuitResultType";
import {Require} from "../Require";
import {scrollTo} from "../../../service/ToolsService";
import {ApiQueryParam, ApiService} from "../../../service/ApiService";
import {step} from "../../../models/step";
import {Loader} from "../Loader";
import CreatableSelect from 'react-select/creatable';
import {stepCompletionResultType} from "../../../types/Api/StepResultType";

type OptionType = {
  value: string;
  label: string;
};

type SubFormDataType = {
  stepId?: string;
  step?: OptionType;
};

type FormDataType = {
  label?: string;
  distance?: number;
  openRunner?: string;
  steps: SubFormDataType[];
};

type CircuitFormProps = {
  circuit?: circuit;
};

export const CircuitForm = ({circuit}: CircuitFormProps) => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [options, setOptions] = useState([] as OptionType[]);

  const {
    control,
    register,
    handleSubmit,
    formState: {errors},
  } = useForm<FormDataType>({
    defaultValues: {
      label: circuit?.label,
      distance: circuit?.distance,
      openRunner: circuit?.openRunner,
      steps: circuit?.steps.map((step: step) => {
        return {
          stepId: step.stepId,
          step: {value: step.label, label: step.label},
        }
      }),
    },
  });

  const {fields, append, remove, move} = useFieldArray({
    control,
    name: "steps",
  });

  const onSortEnd = ({oldIndex, newIndex}: { oldIndex: number; newIndex: number }) => {
    if (oldIndex !== newIndex) {
      move(oldIndex, newIndex);
    }
  };

  const DragHandle = SortableHandle(() => <span className="cursor-grab cursor-pointer"><i className="fa-solid fa-sort"></i></span>);
  // const DragHandle = SortableHandle(() => <span className="cursor-grab cursor-pointer">☰</span>);

  type SortableItemProps = {
    field: SubFormDataType,
    i: number,
  }

  type SortableListProps = {
    steps: SubFormDataType[],
  }

  const SortableItem = SortableElement<SortableItemProps>(({field, i}: SortableItemProps) => {
    return (
      <tr key={i}>
        <td className="align-middle text-center w30px">
          <DragHandle/>
        </td>
        <td>
          <Controller
            name={`steps.${i}.step`}
            control={control}
            rules={{ required: "L'étape est obligatoire" }}
            defaultValue={undefined}
            render={({ field }) => (
              <CreatableSelect
                {...field}
                isClearable
                options={options}
                placeholder="Tapez pour ajouter ou sélectionner..."
                onChange={(val) => field.onChange(val)}
                filterOption={(option, inputValue) => option.label.toLowerCase().includes(inputValue.toLowerCase())}
              />
            )}
          />
          {errors.steps?.[i]?.step && <p className="text-danger">{errors.steps[i].step.message}</p>}
        </td>
        <td className="text-end align-middle w80px">
          <button className="btn btn-xs btn-danger" type="button" onClick={() => remove(i)}>
            Supprimer
          </button>
        </td>
      </tr>
    )
  });

  const SortableList = SortableContainer<SortableListProps>(({steps}: { steps: SubFormDataType[] }) => (
    <tbody>
    {steps.map((field, index) => {
      return (
        <SortableItem field={field} key={index} index={index} i={index}/>
      )
    })}
    </tbody>
  ));

  const onSubmit = async (data: FormDataType) => {
    const n = {
      label: data.label || undefined,
      distance: data.distance || undefined,
      openRunner: data.openRunner || undefined,
    };

    const toDelete = circuit?.steps?.filter(step =>
      !data.steps.some(dSize => dSize.stepId === step.stepId)
    );

    try {
      setSaving(true);

      const result = circuit
        ? await ApiService.patch(`/circuit/${circuit.circuitId}`, {param: () => n}) as circuitResultType
        : await ApiService.post('/circuit', {param: () => n}) as circuitResultType;
      let order = 1;

      for (const del of toDelete ?? []) {
        await ApiService.delete(`/step/${del.stepId}`);
      }

      for (const step of data.steps) {
        let stepApi: {label: string, order: number, circuitId?: string} = {
          label: step.step?.value ?? '',
          order: order
        }

        if (step.stepId) {
          const stepFound = circuit?.steps.find((s: step) => (s.stepId === step.stepId && s.label === step.step?.value && s.order === stepApi.order));

          if (stepFound === undefined) {
            await ApiService.patch(`/step/${step.stepId}`, {param: () => stepApi});
          }
        } else {
          stepApi.circuitId = result.circuitId;

          await ApiService.post(`/step`, {param: () => stepApi});
        }

        order++;
      }
    } catch (e) {
      alert("Une erreur c'est produite.")
    }

    setSaving(false);
    navigate('/admin/route/circuit')
  };

  useEffect(() => {
    const apiFilters: ApiQueryParam = new ApiQueryParam();

    apiFilters.addSort({by: 'label', direction: 'ASC'})
    apiFilters.setCount(0);

    const result = ApiService.get(`/step/completion`, apiFilters) as Promise<stepCompletionResultType[]>;

    result
      .then((r) => {
        setOptions(r.map((step: stepCompletionResultType) => {
          return {
            value: `${step.label}`,
            label: `${step.label}`,
          };
        }));
      })
      .catch(() => {
        alert("Une erreur c'est produite.")
      });
  }, []);

  return (
    <>
      {saving && (<Loader modal={true} message="Enregistrement en cours" />)}
      <form className="p-3" method="post" onSubmit={handleSubmit(onSubmit)}>
        <div className="d-flex">
          <div className="col max-w600px pe-4">
            <div className="mb-3">
              <label className="form-label" htmlFor="form-title">
                Nom <Require/>
              </label>
              <input
                id="form-title"
                type="text"
                className="form-control"
                placeholder="~"
                {...register("label", {
                  required: "Le nom est requis",
                  validate: {
                    minLength: (value) =>
                      (value ?? "").length <= 255 || "Le nom ne doit pas dépasser 255 caractères",
                  },
                })}
              />
              {errors.label && <p className="text-danger">{errors.label.message}</p>}
            </div>
            <div className="mb-3 max-w150px">
              <label className="form-label" htmlFor="form-distance">Distance</label>
              <div className="input-group mb-2 mr-sm-2">
                <input
                  id="form-distance"
                  type="number"
                  className="form-control"
                  placeholder="~"
                  {...register("distance", {
                    setValueAs: (value) => (value === "" ? undefined : Number(value)),
                    validate: {
                      valide: (value) =>
                        value === undefined || !Number.isNaN(value) || "La distance doit être un chiffre",
                    },
                  })}
                />
                <div className="input-group-text">km</div>
              </div>
              {errors.distance && <p className="text-danger">{errors.distance.message}</p>}
            </div>
            <div className="mb-3">
              <label className="form-label" htmlFor="form-openRunner">
              Open Runner
              </label>
              <input
                id="form-openRunner"
                type="text"
                className="form-control"
                placeholder="~"
                {...register("openRunner", {
                  validate: {},
                })}
              />
              {errors.openRunner && <p className="text-danger">{errors.openRunner.message}</p>}
            </div>
          </div>
          <div className="col max-w600px pe-4">
            <div className="mb-3">
              <label className="form-label">Etape</label>
              <table className="table table-responsive">
                <SortableList steps={fields as SubFormDataType[]} onSortEnd={onSortEnd} useDragHandle/>
              </table>
              <div>
                <button type="button" className="btn btn-xs btn-primary" onClick={() => append({})}>
                  Ajouter une étape
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-3">
          <button className="btn btn-sm btn-success me-1" type="submit">
            Enregistrer
          </button>
          <NavLink to={`/admin/route/circuit`} onClick={scrollTo} className="btn btn-sm btn-info">
            Retour aux circuits
          </NavLink>
        </div>
      </form>
    </>
  );
};